import React, { useState } from 'react';
import {
  Hamburger,
  ToolbarButton,
  Avatar,
  Drawer,
  DrawerBody,
  Input,
  Text,
  Link as FluentLink,
  TabList,
  Tab,
  Button,
  makeStyles,
  shorthands,
  tokens,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  MenuGroupHeader,
  Switch,
  Tooltip,
  ToggleButton,
} from '@fluentui/react-components';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Sidebar from '../components/Sidebar';
import LeftRail from '../components/LeftRail';
import MobileBottomNav from '../components/MobileBottomNav';
import MobileNavDrawer from '../components/MobileNavDrawer';
import ChatWindow from '../components/ChatWindow';
import MinimizedChat from '../components/MinimizedChat';
import GlobalSearch from '../components/GlobalSearch';
import DetailsDrawer from '../components/DetailsDrawer';
import '../styles/layout.css';
//import IntercomWidget from '../components/IntercomWidget';
import Header from '../components/Header';
import { getLayoutConfigForRoute } from '../config/toolbars';
import { LayoutProvider, useLayout } from '../contexts/LayoutContext';
import { ChatProvider, useChat } from '../contexts/ChatContext';
import { DebugProvider } from '../contexts/DebugContext';
import { SelectionProvider } from '../contexts/SelectionContext';
import { useTheme, ThemePreference } from '../contexts/ThemeContext';
import DebugToggle from '../components/DebugToggle';
import {
  Home24Regular,
  Document24Regular,
  Alert24Regular,
  Chat24Regular,
  MoreHorizontal24Regular,
  ArrowExpand24Regular,
  Edit24Regular,
  Search24Regular,
  Dismiss24Regular,
  Call24Regular,
  Speaker224Regular,
  ChatBubblesQuestion24Regular,
  PeopleCheckmark24Regular,
  Archive24Regular,
  MailCheckmark24Regular,
  ArrowClockwise24Regular,
  WeatherMoon24Regular,
  WeatherSunny24Regular,
  Desktop24Regular,
  PersonAccounts24Regular,
  PersonEdit24Regular,
  ShieldLock24Regular,
  SignOut24Regular,
  Eye24Regular,
  EyeOff24Regular,
  Checkmark16Regular,
} from '@fluentui/react-icons';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { ProxyRoleProvider, useProxyRole, ROLE_LABELS, ROLE_COLORS, ProxyRole } from '../contexts/ProxyRoleContext';
/*import { useIncomingMessages } from '../hooks/useIncomingMessages';*/
import { apiFetch } from '../lib/apiFetch';
import { isSandbox, isDev } from '../config/appEnv';
import HeaderDeployBadge from '../components/HeaderDeployBadge';

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: React.ReactElement }[] = [
  { value: 'system', label: 'Use system setting', icon: <Desktop24Regular /> },
  { value: 'dark', label: 'Dark', icon: <WeatherMoon24Regular /> },
  { value: 'light', label: 'Light', icon: <WeatherSunny24Regular /> },
];

const useDrawerStyles = makeStyles({
  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.padding('0'),
  },
  drawerTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
  },
  drawerActions: {
    display: 'flex',
    gap: '8px',
  },
  searchBox: {
    ...shorthands.padding('8px', '16px'),
  },
  filterTabs: {
    ...shorthands.padding('0', '16px'),
  },
  conversationList: {
    flexGrow: 1,
    overflowY: 'auto',
    ...shorthands.padding('8px', '0'),
  },
  conversationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  conversationContent: {
    flexGrow: 1,
    minWidth: 0,
  },
  conversationName: {
    fontWeight: '600',
    fontSize: '15px',
    marginBottom: '4px',
  },
  conversationPreview: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  conversationTime: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  footer: {
    ...shorthands.padding('16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke1),
    textAlign: 'center',
  },
  composeWindow: {
    position: 'fixed',
    bottom: '0',
    right: '20px',
    width: '328px',
    height: '455px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px', '8px', '0', '0'),
    boxShadow: tokens.shadow16,
    zIndex: 1100,
    display: 'flex',
    flexDirection: 'column',
  },
  composeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
  },
  composeTitle: {
    fontSize: '16px',
    fontWeight: '600',
  },
  composeToLabel: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
  },
  composeContactList: {
    flexGrow: 1,
    overflowY: 'auto',
  },
  composeContactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  composeContactName: {
    fontSize: '15px',
  },
});

// Sample conversation data
const conversations = [
  {
    id: 1,
    name: 'Carmen Rivera',
    preview: 'The dumpster is ready for pickup tomorrow morning',
    time: '6h',
    avatar: 'CR',
  },
  {
    id: 2,
    name: 'Luis Martínez',
    preview: 'You: Can you confirm the delivery address?',
    time: '1w',
    avatar: 'LM',
  },
  {
    id: 3,
    name: 'Sofía Delgado',
    preview: 'Thanks for the quick response! Really appreciate it',
    time: '1w',
    avatar: 'SD',
  },
  {
    id: 4,
    name: 'Miguel Santos',
    preview: 'Is the 20-yard dumpster still available for rent?',
    time: '2w',
    avatar: 'MS',
  },
  {
    id: 5,
    name: 'Isabella Vega',
    preview: 'Perfect! See you then',
    time: '3w',
    avatar: 'IV',
  },
  {
    id: 6,
    name: 'Rafael Torres',
    preview: 'You sent an attachment.',
    time: '7w',
    avatar: 'RT',
  },
];

// Sample contacts for compose message
const contacts = [
  {
    id: 101,
    name: 'Carmen Rivera',
    avatar: 'CR',
  },
  {
    id: 102,
    name: 'Luis Martínez',
    avatar: 'LM',
  },
  {
    id: 103,
    name: 'Sofía Delgado',
    avatar: 'SD',
  },
  {
    id: 104,
    name: 'Miguel Santos',
    avatar: 'MS',
  },
  {
    id: 105,
    name: 'Isabella Vega',
    avatar: 'IV',
  },
  {
    id: 106,
    name: 'Rafael Torres',
    avatar: 'RT',
  },
];

function LayoutContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const drawerStyles = useDrawerStyles();
  const queryClient = useQueryClient();
  const { sidebarOpen, setSidebarOpen } = useLayout();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { themeMode, themePreference, setThemePreference } = useTheme();
  const { user, logout } = useAdminAuth();
  const { proxyRole, setProxyRole } = useProxyRole();

  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [composeMessageOpen, setComposeMessageOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [incomingCallSounds, setIncomingCallSounds] = useState(true);
  const [messageSounds, setMessageSounds] = useState(true);

  const [popupNewMessages, setPopupNewMessages] = useState(true);
  // Profile menu build stamp: clicking the build # copies it to the clipboard
  // (brief "Copied!" confirmation via this flag); the build timestamp is always
  // shown below it.
  const [buildShaCopied, setBuildShaCopied] = useState(false);
  // Sandbox email delivery mode, shown beside the SANDBOX badge so the
  // suppress/redirect/live state is never out of sight. Updated live when the
  // Settings toggle dispatches 'sandboxEmailModeChanged'.
  const [emailMode, setEmailMode] = useState<string>('');
  const { openChat, openChatWindows, minimizedChats, closeChat, minimizeChat, closeMinimizedChat } = useChat();
  /*useIncomingMessages(messageSounds);*/

  React.useEffect(() => {
    if (!isSandbox()) return;
    let active = true;
    const load = async () => {
      try {
        const res = await apiFetch('/company');
        if (res.ok && active) {
          const c = await res.json();
          setEmailMode(c.email_delivery_mode ?? '');
        }
      } catch { /* badge just omits the mode on failure */ }
    };
    load();
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.mode != null) setEmailMode(detail.mode);
    };
    window.addEventListener('sandboxEmailModeChanged', onChange);
    return () => { active = false; window.removeEventListener('sandboxEmailModeChanged', onChange); };
  }, []);

  const handleOpenChat = (conversation: { id: number, name: string, avatar: string, preview?: string }) => {
    openChat(conversation);
    setChatDrawerOpen(false);
  };

  const handleSelectContact = (contact: { id: number, name: string, avatar: string }) => {
    // Create a conversation object from the contact
    const conversation = {
      id: contact.id,
      name: contact.name,
      avatar: contact.avatar,
      preview: '',
    };

    // Open chat window with this contact
    handleOpenChat(conversation);

    // Close the compose window
    setComposeMessageOpen(false);
  };

  return (
    <>
      <div className={`global-header ${themeMode === 'dark' ? 'dark-mode' : ''} ${isSandbox() ? 'sandbox' : ''} ${isDev() ? 'dev' : ''}`}>
        <div className="global-header-left">
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <img src="/logo-tradewinds.png" alt="Tradewinds Mechanical Logo" className="company-logo" style={{ objectFit: 'contain' }} />
          </Link>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span className="company-name">Tradewinds Mechanical</span>
          </Link>
          {isSandbox() && (
            <span
              className="sandbox-badge"
              title={`Email: ${emailMode || 'unknown'}`}
            >
              SANDBOX{emailMode ? ` · Email: ${emailMode === 'live' ? 'LIVE' : emailMode === 'redirect' ? 'Redirect' : 'Suppressed'}` : ''}
            </span>
          )}
          {/* Dev tier (VITE_APP_ENV=dev) — shares the sandbox data but runs the
              develop branch on its own URL. Distinct badge so it's never mistaken
              for sandbox or prod. The backend runs APP_ENV=sandbox (see
              docs/reference/environments.md), so this is a frontend-only label. */}
          {isDev() && (
            <span className="sandbox-badge dev-badge" title="Dev tier — develop branch on sandbox data">
              DEV
            </span>
          )}
          {/* Live Vercel deploy status — non-prod only (next to the env badge).
              Always reads prod deploy state; click navigates to /dev for detail. */}
          {(isDev() || isSandbox()) && <HeaderDeployBadge />}
          {proxyRole !== 'driver' && <GlobalSearch />}
        </div>
        <div className="global-header-right">
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                {user?.name && (
                  <span className="header-user-name" style={{ fontSize: '14px', fontWeight: 500, color: '#ffffff' }}>
                    {user.name}{proxyRole && <span style={{ fontWeight: 400, opacity: 0.8 }}> ({ROLE_LABELS[proxyRole]})</span>}
                  </span>
                )}
                <Avatar name={user?.name || 'User'} size={32} image={user?.avatar_url ? { src: user.avatar_url } : undefined} />
              </div>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {user?.owner && (
                  <>
                    <MenuItem icon={<PersonEdit24Regular />} onClick={() => navigate('/profile')}>
                      Edit profile
                    </MenuItem>
                    <MenuItem icon={<PersonAccounts24Regular />} onClick={() => navigate('/settings/account')}>
                      Account settings
                    </MenuItem>
                    <MenuItem icon={<ShieldLock24Regular />} onClick={() => navigate('/settings/security')}>
                      Security
                    </MenuItem>
                    <MenuDivider />
                    <MenuGroupHeader>View as role</MenuGroupHeader>
                    {(Object.keys(ROLE_LABELS) as ProxyRole[]).map(role => (
                      <MenuItem
                        key={role}
                        icon={proxyRole === role ? <Checkmark16Regular /> : <Eye24Regular style={{ opacity: 0 }} />}
                        onClick={() => setProxyRole(proxyRole === role ? null : role)}
                      >
                        {ROLE_LABELS[role]}
                      </MenuItem>
                    ))}
                    {proxyRole && (
                      <MenuItem icon={<EyeOff24Regular />} onClick={() => setProxyRole(null)}>
                        Exit preview
                      </MenuItem>
                    )}
                    <MenuDivider />
                  </>
                )}
                <MenuGroupHeader>Theme</MenuGroupHeader>
                {/* Icon-only segmented control. Lives outside MenuItem (with
                    stopPropagation) so picking a theme doesn't close the menu. */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'flex', gap: '4px', padding: '4px 12px 8px' }}
                >
                  {THEME_OPTIONS.map((opt) => (
                    <Tooltip key={opt.value} content={opt.label} relationship="label" withArrow>
                      <ToggleButton
                        appearance="subtle"
                        checked={themePreference === opt.value}
                        icon={opt.icon}
                        aria-label={opt.label}
                        onClick={() => setThemePreference(opt.value)}
                      />
                    </Tooltip>
                  ))}
                </div>
                <MenuDivider />
                <MenuItem icon={<SignOut24Regular />} onClick={() => { logout(); navigate('/login'); }}>
                  Sign out
                </MenuItem>
              </MenuList>
              {/* Build stamp sits below MenuList (not inside it) so clicking the
                  build # to copy it doesn't close the popover. The timestamp is
                  always shown below the #. */}
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
                  padding: '8px 12px',
                  fontSize: '11px',
                  color: tokens.colorNeutralForeground3,
                  textAlign: 'center',
                  fontFamily: 'monospace',
                  userSelect: 'none',
                }}
              >
                <div
                  onClick={() => {
                    if (!navigator.clipboard) return;
                    navigator.clipboard.writeText(__APP_BUILD_SHA__)
                      .then(() => {
                        setBuildShaCopied(true);
                        setTimeout(() => setBuildShaCopied(false), 1500);
                      })
                      .catch(() => { /* clipboard blocked — no-op */ });
                  }}
                  title="Click to copy build #"
                  style={{ cursor: 'pointer' }}
                >
                  <strong style={{ color: tokens.colorNeutralForeground1 }}>
                    {buildShaCopied ? 'Copied!' : __APP_BUILD_SHA__}
                  </strong>
                </div>
                <div style={{ marginTop: '2px' }}>
                  {`${__APP_BUILT_AT__.replace('T', ' ').slice(0, 16)} UTC`}
                </div>
              </div>
            </MenuPopover>
          </Menu>
        </div>
      </div>


      {/* Chat Drawer Backdrop */}
      {chatDrawerOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
          }}
          onClick={() => setChatDrawerOpen(false)}
        />
      )}

      {/* Chat Drawer */}
      <Drawer
        type="overlay"
        position="end"
        size="small"
        open={chatDrawerOpen}
        onOpenChange={(_, { open }) => setChatDrawerOpen(open)}
        modalType="non-modal"
        style={{ top: '48px', height: 'calc(100dvh - 48px)' }}
      >
        <DrawerBody className={drawerStyles.drawerContent}>
          {/* Header with title and actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px' }}>
            <Text className={drawerStyles.drawerTitle}>Chats</Text>
            <div className={drawerStyles.drawerActions}>
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    appearance="subtle"
                    icon={<MoreHorizontal24Regular />}
                    aria-label="More options"
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <div style={{ padding: '16px', paddingBottom: '8px' }}>
                      <Text weight="bold" size={500}>Chat settings</Text>
                      <Text size={300} style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: '4px' }}>
                        Customize your Message Center experience.
                      </Text>
                    </div>
                    <MenuDivider />
                    <MenuItem
                      icon={<Call24Regular />}
                      style={{
                        minWidth: '450px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                      persistOnClick
                    >
                      <span style={{ flex: 1, marginLeft: '8px' }}>Incoming call sounds</span>
                      <div style={{ marginLeft: 'auto' }}>
                        <Switch
                          checked={incomingCallSounds}
                          onChange={(e, data) => {
                            e.stopPropagation();
                            setIncomingCallSounds(data.checked);
                          }}
                        />
                      </div>
                    </MenuItem>
                    <MenuItem
                      icon={<Speaker224Regular />}
                      style={{
                        minWidth: '450px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                      persistOnClick
                    >
                      <span style={{ flex: 1, marginLeft: '8px' }}>Message sounds</span>
                      <div style={{ marginLeft: 'auto' }}>
                        <Switch
                          checked={messageSounds}
                          onChange={(e, data) => {
                            e.stopPropagation();
                            setMessageSounds(data.checked);
                          }}
                        />
                      </div>
                    </MenuItem>
                    <MenuItem
                      icon={<ChatBubblesQuestion24Regular />}
                      style={{
                        minWidth: '450px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                      persistOnClick
                    >
                      <span style={{ flex: 1, marginLeft: '8px' }}>Pop-up new messages</span>
                      <div style={{ marginLeft: 'auto' }}>
                        <Switch
                          checked={popupNewMessages}
                          onChange={(e, data) => {
                            e.stopPropagation();
                            setPopupNewMessages(data.checked);
                          }}
                        />
                      </div>
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem icon={<PeopleCheckmark24Regular />}>
                      Active Status: ON
                    </MenuItem>
                    <MenuItem icon={<Archive24Regular />}>
                      Archived chats
                    </MenuItem>
                    <MenuItem icon={<MailCheckmark24Regular />}>
                      Message delivery settings
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
              <Button
                appearance="subtle"
                icon={<ArrowExpand24Regular />}
                aria-label="Expand"
                onClick={() => {
                  navigate('/messaging');
                  setChatDrawerOpen(false);
                }}
              />
              <Button
                appearance="subtle"
                icon={<Edit24Regular />}
                aria-label="New message"
                onClick={() => {
                  setComposeMessageOpen(true);
                  setChatDrawerOpen(false);
                }}
              />
            </div>
          </div>

          {/* Search box */}
          <div className={drawerStyles.searchBox}>
            <Input
              contentBefore={<Search24Regular />}
              placeholder="Search Messages"
              style={{ width: '100%' }}
            />
          </div>

          {/* Filter tabs */}
          <div className={drawerStyles.filterTabs}>
            <TabList
              selectedValue={selectedFilter}
              onTabSelect={(_, data) => setSelectedFilter(data.value as string)}
            >
              <Tab value="all">All</Tab>
              <Tab value="unread">Unread</Tab>
            </TabList>
          </div>

          {/* Conversation list */}
          <div className={drawerStyles.conversationList}>
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                className={drawerStyles.conversationItem}
                onClick={() => handleOpenChat(conversation)}
              >
                <Avatar
                  name={conversation.name}
                  color="colorful"
                  size={56}
                />
                <div className={drawerStyles.conversationContent}>
                  <div className={drawerStyles.conversationName}>{conversation.name}</div>
                  {conversation.preview && (
                    <div className={drawerStyles.conversationPreview}>{conversation.preview}</div>
                  )}
                </div>
                <div className={drawerStyles.conversationTime}>{conversation.time}</div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className={drawerStyles.footer}>
            <FluentLink href="/Messaging">See all in Message Center</FluentLink>
          </div>
        </DrawerBody>
      </Drawer>

      <MobileBottomNav onOpenDrawer={() => setMobileNavOpen(true)} />
      <MobileNavDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      <div className="layout">
        <LeftRail onNavigate={() => setSidebarOpen(true)} />
        <div className="layout-right">
          {/* The floating global Header band. Routes that render their own
              inline Header (unified-surface workspace cards) or no header at
              all opt out via `suppressGlobalHeader` in the route's layoutConfig
              (config/toolbars) — the single source of truth, so new pages add
              one entry there instead of editing this condition. */}
          {!getLayoutConfigForRoute(location.pathname).suppressGlobalHeader && (
            <div className="page-header">
              <Header />
            </div>
          )}
          <div className="layout-body">
            <div className="content-area">
              <Sidebar isOpen={sidebarOpen} />
              <main className={`main-content ${location.pathname === '/operations-map' ? 'main-content-no-scroll' : ''} ${location.pathname === '/' ? 'main-content-home' : ''}`}>
                <Outlet />
              </main>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Windows */}
      {openChatWindows.map((chat, index) => (
        <ChatWindow
          key={chat.id}
          conversation={chat}
          position={index}
          onClose={() => closeChat(chat.id)}
          onMinimize={() => minimizeChat(chat.id)}
          outgoingSenderType="staff"
          onFetchMessages={chat.conversationId ? async (convId) => {
            const token = localStorage.getItem('admin_token');
            const r = await apiFetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/conversations/${convId}/messages`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!r.ok) throw new Error('Failed to fetch');
            return r.json();
          } : undefined}
          onSend={chat.conversationId ? async (convId, text) => {
            const token = localStorage.getItem('admin_token');
            const r = await apiFetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/conversations/${convId}/messages`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ text }),
            });
            if (!r.ok) throw new Error('Failed to send');
            return r.json();
          } : undefined}
        />
      ))}

      {/* Minimized Chats */}
      {minimizedChats.map((chat, index) => (
        <MinimizedChat
          key={chat.id}
          conversation={chat}
          position={index}
          onClick={() => openChat(chat)}
          onClose={() => closeMinimizedChat(chat.id)}
        />
      ))}

      {/* Compose Message Window */}
      {composeMessageOpen && (
        <div className={drawerStyles.composeWindow}>
          <div className={drawerStyles.composeHeader}>
            <Text className={drawerStyles.composeTitle}>New message</Text>
            <Button
              appearance="transparent"
              icon={<Dismiss24Regular />}
              aria-label="Close"
              onClick={() => setComposeMessageOpen(false)}
            />
          </div>
          <div className={drawerStyles.composeToLabel}>To:</div>
          <div className={drawerStyles.composeContactList}>
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className={drawerStyles.composeContactItem}
                onClick={() => handleSelectContact(contact)}
              >
                <Avatar
                  name={contact.name}
                  color="colorful"
                  size={40}
                />
                <Text className={drawerStyles.composeContactName}>{contact.name}</Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Drawer - used by DataTable column filters */}
      <DetailsDrawer />
    </>
  );
}

export default function Layout() {
  return (
    <LayoutProvider>
      <SelectionProvider>
        <ChatProvider>
          <LayoutContent />
        </ChatProvider>
      </SelectionProvider>
    </LayoutProvider>
  );
}